import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

const SlidingPane = ( {
  index,
  activeSlidingPaneIndex,
  backBtnText,
  backBtnClassName,
  setActiveSlidingPaneIndex,
  children,
  setPaneWidths,
} ) => {
  const isPrevious = index === ( activeSlidingPaneIndex - 1 );
  const isActivePane = index === activeSlidingPaneIndex;

  const pane = useRef();
  const [ measuredWidth, setMeasuredWidth ] = useState( 0 );

  // Measure pane width including margins
  const measureWidth = useCallback( () => {
    const currentPane = pane?.current;
    if ( !currentPane ) return;

    const currentPageStyle = window.getComputedStyle( currentPane );
    const marginLeft = parseFloat( currentPageStyle?.marginLeft ) || 0;
    const marginRight = parseFloat( currentPageStyle?.marginRight ) || 0;
    const totalWidth = currentPane.offsetWidth + marginLeft + marginRight;

    if ( totalWidth !== measuredWidth ) {
      setMeasuredWidth( totalWidth );
    }
  }, [ measuredWidth ] );

  // Use ResizeObserver to detect content size changes
  useEffect( () => {
    const currentPane = pane?.current;
    if ( !currentPane ) return undefined;

    // Initial measurement
    measureWidth();

    // Observe size changes - use requestAnimationFrame to avoid
    // "ResizeObserver loop completed with undelivered notifications" error
    let rafId = null;
    const resizeObserver = new ResizeObserver( () => {
      if ( rafId ) {
        cancelAnimationFrame( rafId );
      }
      rafId = requestAnimationFrame( () => {
        measureWidth();
      } );
    } );

    resizeObserver.observe( currentPane );

    return () => {
      if ( rafId ) {
        cancelAnimationFrame( rafId );
      }
      resizeObserver.disconnect();
    };
  }, [ measureWidth ] );

  // Update paneWidths when measured width changes
  useEffect( () => {
    if ( measuredWidth > 0 ) {
      setPaneWidths( ( prev ) => ( {
        ...prev,
        [index]: measuredWidth,
      } ) );
    }
  }, [ measuredWidth, index, setPaneWidths ] );

  const btnOnClick = () => {
    setActiveSlidingPaneIndex( index - 1 );
  };

  return (
    <div
      ref={pane}
      className={classNames(
        'slidingPane',
        { 'slidingPane--active': isActivePane },
        { 'slidingPane--disabled': isPrevious },
      )}
    >
      { backBtnText && (
        <button
          type="button"
          className={classNames(
            'slidingPane__backBtn',
            {
              'slidingPane__backBtn--disabled': isPrevious,
              [`${backBtnClassName}`]: backBtnClassName,
            },
          )}
          aria-label="Go back to previous panel"
          onClick={btnOnClick}
        >
          { backBtnText }
        </button>
      ) }

      { children }

      { isPrevious && (
        <button
          type="button"
          className="slidingPane__backContent"
          aria-label="Go back to previous panel"
          onClick={() => setActiveSlidingPaneIndex( index )}
        >
          { backBtnText }
        </button>
      ) }
    </div>
  );
};

SlidingPane.propTypes = {
  index: PropTypes.number.isRequired,
  activeSlidingPaneIndex: PropTypes.number.isRequired,
  backBtnText: PropTypes.string,
  backBtnClassName: PropTypes.string,
  setActiveSlidingPaneIndex: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  setPaneWidths: PropTypes.func.isRequired,
};

SlidingPane.defaultProps = {
  backBtnText: null,
  backBtnClassName: null,
};

export default SlidingPane;
